import { useQuery } from "@tanstack/react-query";
import { createEmptyClanEffectivePermissions } from "@acme/validators-shared/routers/clan/effectivePermissions";
import { api } from "~/common/utils/trpc.js";
import { normalizeClanTag } from "~/features/search-filters/_utils/normalize-tag.js";
import { getVisibleClanTabs, isClanTabVisible } from "../_utils/clan-tab-routes.js";

interface UseClanDetailAccessParams {
  readonly tag: string;
}

const useClanDetailAccess = ({ tag }: UseClanDetailAccessParams) => {
  const clanTag = normalizeClanTag(tag);

  const detailQuery = useQuery(
    api.clan.getDetailForUser.queryOptions({ clanTag }, { enabled: clanTag.length > 0 })
  );

  const effectivePermissions = detailQuery.data?.effectivePermissions ?? createEmptyClanEffectivePermissions();
  const actingPlayerTag = detailQuery.data?.actingPlayerTag ?? null;
  const canAccessClan = effectivePermissions.canAccessClan;
  const visibleTabs = getVisibleClanTabs(effectivePermissions);
  const auth =
    actingPlayerTag != null && canAccessClan ? { playerTag: actingPlayerTag, clanTag } : null;

  return {
    clanTag,
    auth,
    effectivePermissions,
    visibleTabs,
    isClanTabVisible: (tabValue: Parameters<typeof isClanTabVisible>[0]) =>
      isClanTabVisible(tabValue, effectivePermissions),
    canAccessClan,
    isReady: detailQuery.isSuccess,
    userIsOwner: detailQuery.data?.userIsOwner ?? false,
    ownerTag: detailQuery.data?.ownerTag ?? null,
    playerConnections: detailQuery.data?.playerConnections ?? [],
    clanInfo: detailQuery.data?.clanInfo ?? null,
    activeWar: detailQuery.data?.activeWar ?? null,
    isSyncing: detailQuery.data?.isSyncing ?? false,
    syncFailed: detailQuery.data?.syncFailed ?? false,
  };
};

export default useClanDetailAccess;
